<script lang="ts">
  import { liveQuery } from 'dexie';
  import { writable } from 'svelte/store';
  import { db, type Comment } from '../lib/db';
  import { authState } from '../lib/auth';
  import { logAudit } from '../lib/audit';
  import { newId } from '../lib/ids';
  import { selectedPerson } from '../lib/stores';

  const comments = writable<Comment[]>([]);
  let body = '';

  $: if ($selectedPerson) {
    const id = $selectedPerson.id;
    liveQuery(() =>
      db.comments.where('personId').equals(id).toArray()
        .then((rows) => rows.sort((a, b) => a.createdAt - b.createdAt)),
    ).subscribe((v) => comments.set(v));
  } else {
    comments.set([]);
  }

  async function post() {
    if (!$selectedPerson || !body.trim() || !$authState.email) return;
    const c: Comment = {
      id: newId(),
      treeId: $selectedPerson.treeId,
      personId: $selectedPerson.id,
      author: $authState.email,
      body: body.trim(),
      createdAt: Date.now(),
    };
    await db.comments.add(c);
    await logAudit('comment.create', { treeId: c.treeId, targetId: c.id, after: c });
    body = '';
  }

  async function remove(c: Comment) {
    if (!confirm('Delete this comment?')) return;
    await db.comments.delete(c.id);
    await logAudit('comment.delete', { treeId: c.treeId, targetId: c.id, before: c });
  }
</script>

{#if $selectedPerson}
  <div class="comments card subtle">
    <div class="row head-row">
      <strong>Discussion</strong>
      <span class="chip">{$comments.length}</span>
    </div>
    <p class="dim small">
      Collaborators can suggest corrections, attach sources, or flag missing info.
    </p>
    <ul>
      {#each $comments as c (c.id)}
        <li>
          <header>
            <span class="author mono">{c.author}</span>
            <span class="dim small mono">{new Date(c.createdAt).toISOString().slice(0, 16).replace('T', ' ')}</span>
          </header>
          <div class="body">{c.body}</div>
          {#if $authState.email === c.author || $authState.status === 'admin'}
            <button class="ghost small danger" on:click={() => remove(c)}>delete</button>
          {/if}
        </li>
      {/each}
      {#if !$comments.length}<li class="dim small">no comments yet.</li>{/if}
    </ul>
    <form on:submit|preventDefault={post} class="stack">
      <textarea placeholder="add a correction, a source, a missing date…" bind:value={body}></textarea>
      <div class="row"><button class="primary small" type="submit" disabled={!body.trim()}>post →</button></div>
    </form>
  </div>
{/if}

<style>
  .comments { margin-top: 12px; }
  .small { font-size: var(--ls-step-0); }
  .head-row { justify-content: space-between; }
  ul { list-style: none; padding: 0; margin: 10px 0; }
  li { padding: 8px 0; border-bottom: 1px dashed var(--ls-border); }
  li:last-child { border-bottom: none; }
  header { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
  .author { font-size: 12px; color: var(--ls-text); }
  .body { white-space: pre-wrap; margin: 6px 0; font-size: var(--ls-step-0); }
</style>
